import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Mountain, CloudFog, Hand, Snowflake } from "lucide-react";
import { useI18n } from "@/i18n/LanguageContext";

const EASE = [0.16, 1, 0.3, 1];

const PLANTATION_IMG =
  "https://images.unsplash.com/photo-1701735513192-bc7248978c51?q=80&w=2000&auto=format&fit=crop";

const ICONS = [CloudFog, Mountain, Hand, Snowflake];
const ALTITUDE_MARKS = ["1 600", "1 750", "1 850", "1 950", "2 100"];

export default function Origin() {
  const imgRef = useRef(null);
  const { t } = useI18n();
  const o = t.origin;
  const { scrollYProgress } = useScroll({
    target: imgRef,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="origine" className="border-y border-pine/10 bg-creamd">
      <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 md:py-36">
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: EASE }}
          >
            <div className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-copper">
              {o.eyebrow}
            </div>
            <h2 className="font-serif text-3xl leading-tight tracking-tight text-pine sm:text-5xl">
              {o.titleA} <span className="italic text-copper">{o.titleEm}</span>
            </h2>
            <p className="mt-6 text-base font-light leading-relaxed text-sand sm:text-lg">
              {o.text}
            </p>

            <div className="mt-10 grid gap-6 sm:grid-cols-2">
              {o.factors.map((f, i) => {
                const Icon = ICONS[i];
                return (
                  <motion.div
                    key={f.title}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: i * 0.1, ease: EASE }}
                    data-testid={`origin-factor-${i}`}
                    className="border border-pine/10 bg-paper p-5 transition-colors duration-300 hover:border-copper/40"
                  >
                    <Icon className="h-5 w-5 text-copper" />
                    <h3 className="mt-3 font-serif text-xl text-pine">{f.title}</h3>
                    <p className="mt-2 text-sm font-light leading-relaxed text-sand">
                      {f.text}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            ref={imgRef}
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: EASE }}
            className="relative"
          >
            <div className="overflow-hidden border border-copper/30">
              <motion.img
                style={{ y: imgY }}
                src={PLANTATION_IMG}
                alt="Parcelles de café en altitude — Sierra"
                loading="lazy"
                className="h-[420px] w-full scale-110 object-cover sm:h-[560px]"
              />
            </div>
            <div className="absolute bottom-4 left-4 bg-paper/90 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-copper backdrop-blur-sm">
              {o.caption}
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: EASE }}
          data-testid="altitude-scale"
          className="mt-20"
        >
          <div className="mb-6 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-sand">
            <span>{o.scaleLeft}</span>
            <span>{o.scaleRight}</span>
          </div>
          <div className="relative h-px bg-gradient-to-r from-sand/40 via-copper/60 to-pine">
            <div className="absolute -top-[5px] left-[52%] h-[11px] w-[11px] rounded-full bg-copper shadow-[0_0_18px_rgba(176,104,44,0.6)]" />
            <div className="absolute -top-10 left-[52%] -translate-x-1/2 whitespace-nowrap border border-copper/40 bg-paper px-3 py-1 font-mono text-[10px] tracking-[0.2em] text-copper">
              Monte Yolli — 1 850m
            </div>
          </div>
          <div className="mt-4 flex justify-between font-mono text-xs text-sand">
            {ALTITUDE_MARKS.map((m) => (
              <span key={m}>{m}m</span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
